import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Şahıs şirketinde 2026 gelir vergisi dilimleri nelerdir?',
    answer: '2026 yılı için gelir vergisi dilimleri: 158.000 TL\'ye kadar %15, 330.000 TL\'ye kadar %20, 800.000 TL\'ye kadar %27, 4.300.000 TL\'ye kadar %35, üzeri %40 olarak uygulanmaktadır.',
  },
  {
    question: 'Şahıs şirketinde KDV oranları nasıl belirlenir?',
    answer: 'KDV oranları faaliyet konusuna göre değişir. Genel oran %20\'dir. Temel gıda, ilaç gibi bazı ürünlerde %1 veya %10 indirimli oran uygulanabilir.',
  },
  {
    question: 'Şahıs şirketinde vergi maliyeti ne kadar? (2026)',
    answer: 'Vergi maliyeti gelire ve faaliyet türüne göre değişir. Yıllık 150.000 TL gelir için yaklaşık %15–17 efektif vergi oranı beklenir. Yüksek gelirlerde bu oran %35–40\'a çıkabilir.',
  },
  {
    question: 'Basit usul mükellefler vergi öder mi?',
    answer: 'Basit usul mükellefler gelir vergisinden muaf tutulmaktadır. Ancak KDV\'den de muaf olduklarından fatura düzenleyemezler. Belirli ciro sınırlarını aşanlar gerçek usule geçmek zorundadır.',
  },
  {
    question: 'Şahıs şirketinde geçici vergi nedir?',
    answer: 'Gerçek usul mükellefleri yıl içinde 3 aylık dönemler halinde geçici vergi öder. Bu ödemeler yıl sonu beyannamesiyle mahsup edilir. Geçici vergi oranı gelir vergisi dilimlerine göre hesaplanır.',
  },
  {
    question: 'Şahıs şirketinde Bağ-Kur primi vergi matrahını düşürür mü?',
    answer: 'Evet, ödenen Bağ-Kur primleri gelir vergisi matrahından indirilebilir. Bu durum vergi yükünü azaltır. Detaylı hesaplama için mali müşavirinize danışmanız önerilir.',
  },
];

const brackets = [
  { range: '0 – 158.000 ₺', rate: '%15', color: 'bg-emerald-100 text-emerald-700' },
  { range: '158.001 – 330.000 ₺', rate: '%20', color: 'bg-yellow-100 text-yellow-700' },
  { range: '330.001 – 800.000 ₺', rate: '%27', color: 'bg-orange-100 text-orange-700' },
  { range: '800.001 – 4.300.000 ₺', rate: '%35', color: 'bg-red-100 text-red-700' },
  { range: '4.300.001 ₺ ve üzeri', rate: '%40', color: 'bg-rose-100 text-rose-700' },
];

const kdvRates = [
  { category: 'Genel Oran', rate: '%20', examples: 'Çoğu ürün ve hizmet' },
  { category: 'İndirimli Oran', rate: '%10', examples: 'Bazı gıda, tarım ürünleri' },
  { category: 'Düşük Oran', rate: '%1', examples: 'Temel gıda, ilaç, konut' },
];

export default function SahisSirketiVergiOranlariPage() {
  const [income, setIncome] = useState(200000);

  useEffect(() => {
    window.scrollTo({ top: 0 });

    document.title = 'Şahıs Şirketi Vergi Oranları 2026 | Gelir Vergisi Dilimleri | Özgür Çakmak SMMM';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Şahıs şirketi vergi oranları 2026: Güncel gelir vergisi dilimleri (%15-%40), KDV oranları, Bağ-Kur primleri ve vergi yükü hesaplayıcısı ile tahmini verginizi öğrenin.');
    setMeta('keywords', 'şahıs şirketi vergi oranları 2026, gelir vergisi dilimleri, KDV oranları, vergi hesaplama, şahıs şirketi vergi maliyeti');
    setMeta('og:title', 'Şahıs Şirketi Vergi Oranları 2026 | Gelir Vergisi Dilimleri', true);
    setMeta('og:description', '2026 güncel gelir vergisi dilimleri, KDV oranları ve interaktif vergi hesaplayıcısı ile vergi yükünüzü öğrenin.', true);
    setMeta('og:type', 'article', true);
    setMeta('og:url', 'https://ozgurcakmak.com.tr/sahis-sirketi-vergi-oranlari', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', 'https://ozgurcakmak.com.tr/sahis-sirketi-vergi-oranlari');

    const existingSchema = document.getElementById('faq-schema-ssvo');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.id = 'faq-schema-ssvo';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqs.map((f) => ({
        '@type': 'Question',
        'name': f.question,
        'acceptedAnswer': { '@type': 'Answer', 'text': f.answer },
      })),
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('faq-schema-ssvo');
      if (s) s.remove();
    };
  }, []);

  const calcTax = (inc: number) => {
    let tax = 0;
    const b = [
      { limit: 158000, rate: 0.15 },
      { limit: 330000, rate: 0.20 },
      { limit: 800000, rate: 0.27 },
      { limit: 4300000, rate: 0.35 },
      { limit: Infinity, rate: 0.40 },
    ];
    let rem = inc, prev = 0;
    for (const br of b) {
      if (rem <= 0) break;
      const chunk = Math.min(rem, br.limit - prev);
      tax += chunk * br.rate;
      rem -= chunk;
      prev = br.limit;
    }
    return Math.round(tax);
  };

  const tax = calcTax(income);
  const net = income - tax;
  const eff = income > 0 ? ((tax / income) * 100).toFixed(1) : '0.0';

  return (
    <div className="min-h-screen bg-white">
      <InfoNavbar />

      <section className="relative pt-20 min-h-[420px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/2967078d4011825606cd29c369d45386.jpg" alt="Şahıs Şirketi Vergi Oranları 2026" className="w-full h-full object-cover object-top" loading="eager" decoding="async" />
          <div className="absolute inset-0 bg-[#0A2540]/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 w-full">
          <nav className="flex items-center gap-2 mb-5" aria-label="breadcrumb">
            <Link to="/" className="text-white/50 text-xs hover:text-white/80 transition-colors">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line text-white/30 text-xs"></i>
            <span className="text-white/70 text-xs">Şahıs Şirketi Vergi Oranları 2026</span>
          </nav>
          <div className="flex flex-wrap gap-2 mb-5">
            {['2026 Güncel', 'Vergi Dilimleri', 'KDV Oranları', 'Maliyeti', 'Hesaplama'].map((kw) => (
              <span key={kw} className="px-3 py-1 bg-white/10 border border-white/20 text-white/80 text-xs rounded-full">{kw}</span>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 max-w-2xl leading-tight">Şahıs Şirketi Vergi Oranları 2026</h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed mb-8">Gelir vergisi dilimleri, KDV oranları ve diğer yükümlülükler güncel rakamlarla.</p>
          <div className="flex flex-wrap gap-3">
            <a href="#iletisim-cta" onClick={(e) => { e.preventDefault(); document.getElementById('iletisim-cta')?.scrollIntoView({ behavior: 'smooth' }); }} className="px-6 py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap">Bilgi Al</a>
            <a href="https://wa.me/905300953230" target="_blank" rel="nofollow noreferrer" className="px-6 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-whatsapp-line"></i>WhatsApp</a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <p className="text-gray-600 text-base leading-relaxed text-center">
            Şahıs şirketlerinde vergi yükümlülükleri, faaliyet türüne ve yıllık gelire göre farklılık gösterir. 2026 yılı itibarıyla güncel <strong>gelir vergisi dilimleri</strong>, KDV oranları ve diğer yükümlülükler aşağıda açıklanmaktadır. Durumunuza özel bilgi almak için iletişime geçebilirsiniz.
          </p>
        </div>
      </section>

      {/* Vergi Dilimleri */}
      <section className="py-14 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">2026 Güncel</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Gelir Vergisi Dilimleri</h2>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-3 bg-[#0A2540] px-6 py-3">
              <span className="text-white/70 text-xs font-semibold">Gelir Aralığı</span>
              <span className="text-white/70 text-xs font-semibold text-center">Vergi Oranı</span>
              <span className="text-white/70 text-xs font-semibold text-right">Durum</span>
            </div>
            {brackets.map((b, i) => (
              <div key={i} className={`grid grid-cols-3 px-6 py-4 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5F7FA]'}`}>
                <span className="text-[#0A2540] text-sm font-medium">{b.range}</span>
                <div className="flex justify-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${b.color}`}>{b.rate}</span>
                </div>
                <span className="text-gray-400 text-xs text-right">{i === 0 ? 'En düşük' : i === brackets.length - 1 ? 'En yüksek' : 'Orta dilim'}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-xs mt-4 text-center">* Vergi dilimleri her yıl yeniden değerleme oranında güncellenmektedir.</p>
        </div>
      </section>

      {/* KDV Oranları */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">KDV</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">KDV Oranları</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {kdvRates.map((r, i) => (
              <div key={i} className="bg-[#F5F7FA] rounded-2xl p-6 border border-gray-100 text-center">
                <div className="text-4xl font-black text-[#0A2540] mb-2">{r.rate}</div>
                <h3 className="text-[#0A2540] font-semibold text-sm mb-2">{r.category}</h3>
                <p className="text-gray-500 text-xs">{r.examples}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hesaplama Aracı */}
      <section className="py-14 bg-[#F5F7FA]">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Hesaplama</span>
            <h2 className="text-2xl font-bold text-[#0A2540] mt-3">Vergi Yükü Hesaplayıcı</h2>
            <p className="text-gray-500 text-sm mt-2">Yıllık gelirinize göre tahmini vergi yükünüzü hesaplayın.</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label className="text-[#0A2540] text-sm font-medium">Yıllık Gelir</label>
                <span className="text-[#0A2540] font-bold text-sm">{income.toLocaleString('tr-TR')} ₺</span>
              </div>
              <input type="range" min={50000} max={2000000} step={10000} value={income} onChange={(e) => setIncome(Number(e.target.value))} className="w-full accent-[#0A2540]" />
              <div className="flex justify-between text-xs text-gray-400 mt-1"><span>50.000 ₺</span><span>2.000.000 ₺</span></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Tahmini Vergi</p>
                <p className="text-[#0A2540] font-bold text-lg">{tax.toLocaleString('tr-TR')} ₺</p>
              </div>
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Net Kalan</p>
                <p className="text-emerald-600 font-bold text-lg">{net.toLocaleString('tr-TR')} ₺</p>
              </div>
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Efektif Oran</p>
                <p className="text-[#0A2540] font-bold text-lg">%{eff}</p>
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-4 text-center">* Tahmini hesaplama. Kesin rakamlar için mali müşavirinize danışınız.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">SSS</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Sık Sorulan Sorular</h2>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      <SoftCTA />
      <div id="iletisim-cta"><ContactCTA /></div>
      <InfoFooter />
      <a href="https://wa.me/905300953230" target="_blank" rel="nofollow noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full cursor-pointer hover:scale-110 transition-transform" aria-label="WhatsApp"><i className="ri-whatsapp-line text-2xl"></i></a>
    </div>
  );
}

function FAQAccordion({ faqs }: { faqs: { question: string; answer: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqs.map((faq, i) => (
        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
          <button className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-[#F5F7FA] transition-colors cursor-pointer" onClick={() => setOpen(open === i ? null : i)}>
            <span className="text-[#0A2540] font-medium text-sm pr-4">{faq.question}</span>
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0"><i className={`text-[#0A2540] text-base ${open === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i></div>
          </button>
          {open === i && <div className="px-6 py-4 bg-[#F5F7FA] border-t border-gray-100"><p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p></div>}
        </div>
      ))}
    </div>
  );
}

function SoftCTA() {
  return (
    <section className="py-10 bg-[#F5F7FA]">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A2540]/5 rounded-full mb-4"><i className="ri-information-line text-[#0A2540] text-sm"></i><span className="text-[#0A2540] text-xs font-medium">Bilgilendirme</span></div>
        <p className="text-gray-600 text-sm leading-relaxed">Bu sayfadaki bilgiler genel rehber niteliğindedir. Durumunuza özel bilgi almak için iletişime geçebilirsiniz.</p>
      </div>
    </section>
  );
}

function ContactCTA() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message || form.message.length > 500) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({ name: form.name, phone: form.phone, message: form.message });
      const res = await fetch('https://readdy.ai/api/form/d7c144qqofe8vli40s7g', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      if (res.ok) { setStatus('success'); setForm({ name: '', phone: '', message: '' }); } else setStatus('error');
    } catch { setStatus('error'); }
  };
  return (
    <section className="py-20 bg-[#0A2540]">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold tracking-widest text-[#60A5FA] uppercase">Ücretsiz Danışmanlık</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-4">Durumunuza Özel Bilgi Alın</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-6">Sorularınızı bize iletin, uzman ekibimiz en kısa sürede size dönüş yapsın.</p>
            <div className="flex flex-col gap-3">
              {[{ icon: 'ri-phone-line', text: '0 530 095 32 30' }, { icon: 'ri-mail-line', text: 'info@ozgurcakmak.com.tr' }, { icon: 'ri-time-line', text: 'Pzt–Cum: 09:00–18:00' }].map((item) => (
                <div key={item.text} className="flex items-center gap-3"><div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg"><i className={`${item.icon} text-white text-sm`}></i></div><span className="text-white/70 text-sm">{item.text}</span></div>
              ))}
            </div>
          </div>
          <form onSubmit={handleSubmit} data-readdy-form id="vergi-contact-form" className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
            <div><label className="text-white/60 text-xs mb-1.5 block">Ad Soyad</label><input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Adınız Soyadınız" className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors" /></div>
            <div><label className="text-white/60 text-xs mb-1.5 block">Telefon</label><input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="+90 5XX XXX XX XX" className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors" /></div>
            <div>
              <div className="flex justify-between mb-1.5"><label className="text-white/60 text-xs">Mesajınız</label><span className="text-white/30 text-xs">{form.message.length}/500</span></div>
              <textarea name="message" value={form.message} onChange={handleChange} required maxLength={500} rows={4} placeholder="Danışmak istediğiniz konu..." className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors resize-none"></textarea>
            </div>
            {status === 'success' && <div className="flex items-center gap-2 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg"><i className="ri-checkbox-circle-line text-emerald-400"></i><span className="text-emerald-300 text-sm">Mesajınız iletildi.</span></div>}
            {status === 'error' && <div className="p-3 bg-rose-500/20 border border-rose-500/30 rounded-lg"><span className="text-rose-300 text-sm">Bir hata oluştu.</span></div>}
            <button type="submit" disabled={status === 'loading' || form.message.length > 500} className="w-full py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-lg hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">{status === 'loading' ? 'Gönderiliyor...' : 'Mesaj Gönder'}</button>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoNavbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const h = () => setScrolled(window.scrollY > 40); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A2540]/50 backdrop-blur-md border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/20f355b3-57c6-42af-8f1b-e19ef75cee72/a013bf3f-953f-4147-a966-2d3225ff6ac1_zgr-akmak-logo-3.png?v=001ea3c7f21fa91e8ebb91c8becb8de9"
            alt="Özgür Çakmak SMMM Logo"
            className="h-12 w-auto object-contain"
          />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm font-medium text-white/90 hover:text-white transition-colors whitespace-nowrap cursor-pointer">Ana Sayfa</Link>
          <a href="#iletisim-cta" onClick={(e) => { e.preventDefault(); document.getElementById('iletisim-cta')?.scrollIntoView({ behavior: 'smooth' }); }} className="px-5 py-2 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:-translate-y-0.5 transition-all whitespace-nowrap cursor-pointer">İletişime Geç</a>
        </div>
      </div>
    </header>
  );
}

function InfoFooter() {
  const links = [
    { label: 'Şahıs Şirketi Nasıl Kurulur?', href: '/sahis-sirketi-nasil-kurulur' },
    { label: 'Vergi Oranları 2026', href: '/sahis-sirketi-vergi-oranlari' },
    { label: 'Limited mi Şahıs mı?', href: '/limited-mi-sahis-mi' },
    { label: 'E-Fatura Zorunluluğu', href: '/e-fatura-kimler-icin-zorunlu' },
    { label: 'KDV Nedir?', href: '/kdv-nedir-nasil-hesaplanir' },
    { label: 'Vergi Borcu Sorgulama', href: '/vergi-borcu-sorgulama' },
  ];
  return (
    <footer className="bg-[#F5F7FA] border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
              <div className="w-8 h-8 flex items-center justify-center bg-[#0A2540] rounded-lg">
                <i className="ri-bar-chart-box-line text-white text-base"></i>
              </div>
              <span className="font-bold text-[#0A2540] text-base">Özgür Çakmak SMMM</span>
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed">Mali süreçlerinizde güvenilir danışmanlık.</p>
          </div>
          <div>
            <h4 className="text-[#0A2540] font-semibold text-sm mb-3">Bilgi Merkezi</h4>
            <ul className="space-y-2">{links.map((l) => (<li key={l.href}><Link to={l.href} className="text-gray-500 text-xs hover:text-[#0A2540] transition-colors cursor-pointer">{l.label}</Link></li>))}</ul>
          </div>
          <div>
            <h4 className="text-[#0A2540] font-semibold text-sm mb-3">İletişim</h4>
            <div className="space-y-2">{[{ icon: 'ri-phone-line', text: '0 530 095 32 30' }, { icon: 'ri-mail-line', text: 'info@ozgurcakmak.com.tr' }].map((item) => (<div key={item.text} className="flex items-center gap-2"><div className="w-5 h-5 flex items-center justify-center text-gray-400"><i className={`${item.icon} text-sm`}></i></div><span className="text-gray-500 text-sm">{item.text}</span></div>))}</div>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 bg-white"><div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2"><p className="text-gray-400 text-xs">© 2026 Özgür Çakmak SMMM. Tüm hakları saklıdır.</p><Link to="/" className="text-gray-400 text-xs hover:text-gray-600 transition-colors cursor-pointer">Ana Sayfaya Dön</Link></div></div>
    </footer>
  );
}
